import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import Hls from 'hls.js';
import {
  Play, Pause, Volume2, VolumeX, Maximize, Maximize2, X,
  ArrowLeft, Users, BarChart3, ChevronDown, ChevronUp,
  Bell, BellOff, BookmarkPlus, BookmarkCheck, Radio
} from 'lucide-react';
import { Navbar } from '../components/Navbar';
import LiveTicker from '../components/LiveTicker';
import LiveBadge from '../components/LiveBadge';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const WS_URL = API?.replace('https://', 'wss://').replace('http://', 'ws://');

// Fire-and-forget analytics
const trackEvent = (eventType, matchId, metadata = {}) => {
  const token = localStorage.getItem('token');
  fetch(`${API}/api/analytics/event`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify({ event_type: eventType, match_id: matchId, metadata })
  }).catch(() => {});
};

// Push notification helpers
const isPushSupported = () =>
  'serviceWorker' in navigator && 'PushManager' in window;

const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map(c => c.charCodeAt(0)));
};

// ── GOAL! Broadcast Overlay ───────────────────────────────────────────────────
// Renders over the video with a TV-broadcast "GOAL!" moment. mini=true for mini-player.
const GoalFlash = ({ flash, teams, mini, onDone }) => {
  const [phase, setPhase] = useState('enter'); // enter → show → exit

  useEffect(() => {
    const toShow = requestAnimationFrame(() =>
      setTimeout(() => setPhase('show'), 30)
    );
    const toExit = setTimeout(() => setPhase('exit'), 2600);
    const toDone = setTimeout(onDone, 3100);
    return () => {
      cancelAnimationFrame(toShow);
      clearTimeout(toExit);
      clearTimeout(toDone);
    };
  }, [onDone]);

  const isShow = phase === 'show';
  const isExit = phase === 'exit';

  const overlayStyle = {
    position: 'absolute',
    inset: 0,
    zIndex: 50,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(0,0,0,0.84)',
    backdropFilter: 'blur(6px)',
    WebkitBackdropFilter: 'blur(6px)',
    boxShadow: 'inset 0 0 100px rgba(16,185,129,0.2)',
    transition: 'opacity 0.45s cubic-bezier(0.4,0,0.2,1), transform 0.45s cubic-bezier(0.4,0,0.2,1)',
    opacity: isShow ? 1 : 0,
    transform: isExit ? 'scale(1.07)' : isShow ? 'scale(1)' : 'scale(0.92)',
    pointerEvents: 'none',
  };

  if (mini) {
    return (
      <div style={overlayStyle} data-testid="goal-flash-mini">
        <p style={{ fontSize: '1rem', fontWeight: 900, color: 'white', margin: 0, letterSpacing: '0.05em' }}>
          ⚽ GOAL!
        </p>
        {flash.player && (
          <p style={{ fontSize: '0.6rem', color: '#34d399', margin: '3px 0 0', fontWeight: 700 }}>
            {flash.player}
          </p>
        )}
      </div>
    );
  }

  return (
    <div style={overlayStyle} data-testid="goal-flash-overlay">
      {/* Emerald pulse rings */}
      <div style={{
        position: 'absolute',
        width: '300px', height: '300px',
        borderRadius: '50%',
        border: '2px solid rgba(16,185,129,0.3)',
        animation: isShow ? 'ping 1.2s cubic-bezier(0,0,0.2,1) infinite' : 'none',
      }} />

      <div style={{ textAlign: 'center', padding: '0 2rem', position: 'relative' }}>
        {/* GOAL! text */}
        <p style={{
          fontSize: 'clamp(3rem, 9vw, 5.5rem)',
          fontWeight: 900,
          color: 'white',
          letterSpacing: '0.06em',
          margin: '0 0 6px',
          lineHeight: 1,
          textShadow: '0 0 30px rgba(16,185,129,0.9), 0 0 60px rgba(16,185,129,0.5), 0 0 100px rgba(16,185,129,0.3)',
        }}>
          ⚽ GOAL!
        </p>

        {/* Player name */}
        {flash.player && (
          <p style={{
            fontSize: 'clamp(0.9rem, 2.8vw, 1.4rem)',
            color: '#34d399',
            fontWeight: 700,
            margin: '0 0 14px',
            letterSpacing: '0.03em',
          }}>
            {flash.player}
          </p>
        )}

        {/* Score */}
        {teams && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
            <span style={{ fontSize: 'clamp(0.8rem, 2vw, 1rem)', color: 'rgba(255,255,255,0.55)', fontWeight: 600 }}>
              {teams.home?.name}
            </span>
            <span style={{
              fontSize: 'clamp(1.8rem, 5vw, 2.8rem)',
              fontWeight: 900,
              color: 'white',
              fontVariantNumeric: 'tabular-nums',
            }}>
              {teams.home?.score}&thinsp;–&thinsp;{teams.away?.score}
            </span>
            <span style={{ fontSize: 'clamp(0.8rem, 2vw, 1rem)', color: 'rgba(255,255,255,0.55)', fontWeight: 600 }}>
              {teams.away?.name}
            </span>
          </div>
        )}

        {flash.minute && (
          <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.35)', marginTop: '8px' }}>
            {flash.minute}'
          </p>
        )}
      </div>
    </div>
  );
};

// Event visual config
const EVENT_CFG = {
  goal:         { label: 'GOAL',         emoji: '⚽', rowCls: 'border-l-4 border-emerald-500 bg-emerald-500/10', timeCls: 'text-emerald-400 font-bold', labelCls: 'text-emerald-400 font-black tracking-widest text-xs', big: true },
  penalty:      { label: 'PENALTY',      emoji: '⚽', rowCls: 'border-l-4 border-orange-500 bg-orange-500/10',  timeCls: 'text-orange-400 font-bold',  labelCls: 'text-orange-400 font-black tracking-widest text-xs',  big: true },
  red_card:     { label: 'RED CARD',     emoji: '🟥', rowCls: 'border-l-4 border-red-500 bg-red-500/10',        timeCls: 'text-red-400 font-bold',     labelCls: 'text-red-400 font-bold tracking-widest text-xs',      big: true },
  yellow_card:  { label: 'YELLOW CARD',  emoji: '🟨', rowCls: 'border-l-2 border-yellow-400/60 bg-yellow-400/5', timeCls: 'text-yellow-400',           labelCls: 'text-yellow-400 text-xs',                             big: false },
  substitution: { label: 'SUB',          emoji: '↕',  rowCls: 'border-l-2 border-blue-400/40 bg-blue-400/5',    timeCls: 'text-blue-400',              labelCls: 'text-blue-400 text-xs',                               big: false },
  var:          { label: 'VAR REVIEW',   emoji: '📺', rowCls: 'border-l-2 border-purple-400/40 bg-purple-400/5', timeCls: 'text-purple-400',           labelCls: 'text-purple-400 text-xs',                             big: false },
  halftime:     { label: 'HALF TIME',    emoji: '⏸', rowCls: 'border border-white/20 bg-white/5 rounded-lg',   timeCls: 'text-white font-bold',       labelCls: 'text-white font-bold text-xs tracking-widest',        big: true },
  fulltime:     { label: 'FULL TIME',    emoji: '🏁', rowCls: 'border border-white/20 bg-white/5 rounded-lg',   timeCls: 'text-white font-bold',       labelCls: 'text-white font-bold text-xs tracking-widest',        big: true },
  kickoff:      { label: 'KICK OFF',     emoji: '▶', rowCls: 'bg-white/5',                                      timeCls: 'text-white/50',              labelCls: 'text-emerald-500/70 text-xs',                         big: false },
};

// Collapsible section
const AccordionSection = ({ title, icon: Icon, children, testId }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-[#111111] rounded-xl overflow-hidden mb-3">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-4 text-white hover:bg-white/5 transition-colors"
        data-testid={testId || `accordion-${title.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-emerald-500" />}
          <span className="font-semibold text-sm tracking-widest uppercase">{title}</span>
        </div>
        {open
          ? <ChevronUp className="w-4 h-4 text-white/30" />
          : <ChevronDown className="w-4 h-4 text-white/30" />
        }
      </button>
      {open && <div className="px-5 pb-5 border-t border-white/5">{children}</div>}
    </div>
  );
};

// Stat bar row
const StatBar = ({ label, home = 0, away = 0, suffix = '' }) => {
  const total = (home + away) || 1;
  const homeW = (home / total) * 100;
  return (
    <div className="space-y-1.5">
      <div className="flex justify-between items-center text-sm">
        <span className="text-white font-semibold tabular-nums w-10">{home}{suffix}</span>
        <span className="text-white/40 text-xs uppercase tracking-wider flex-1 text-center">{label}</span>
        <span className="text-white font-semibold tabular-nums w-10 text-right">{away}{suffix}</span>
      </div>
      <div className="flex h-1.5 rounded-full overflow-hidden bg-white/10">
        <div className="bg-emerald-500 transition-all duration-700" style={{ width: `${homeW}%` }} />
        <div className="bg-orange-500 transition-all duration-700" style={{ width: `${100 - homeW}%` }} />
      </div>
    </div>
  );
};

// Player row in lineup
const PlayerRow = ({ player, accentColor = 'bg-emerald-600' }) => (
  <div className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-white/5 transition-colors">
    <span className={`w-6 h-6 ${accentColor} rounded text-white text-xs font-bold flex items-center justify-center flex-shrink-0`}>
      {player.number}
    </span>
    <span className="text-white/90 text-sm flex-1 truncate">{player.name}</span>
    {player.is_captain && <span className="text-yellow-400 text-xs">(C)</span>}
    <span className="text-white/30 text-xs flex-shrink-0">{player.position}</span>
  </div>
);

// Up Next match card
const UpNextCard = ({ match: m, idx }) => (
  <Link to={`/match/${m.content_id}`} className="block group" data-testid={`up-next-${idx}`}>
    <div className="bg-[#111111] hover:bg-[#161616] border border-transparent hover:border-emerald-900/50 rounded-xl p-3 transition-all duration-200">
      <div className="flex items-center justify-between mb-2">
        {m.status === 'live'
          ? <LiveBadge variant="compact" />
          : m.status === 'fulltime'
          ? <span className="text-white/30 text-xs font-bold">FT</span>
          : <span className="text-emerald-500 text-xs font-semibold">
              {m.start_time
                ? new Date(m.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                : 'UPCOMING'}
            </span>
        }
        <span className="text-white/20 text-sm group-hover:text-emerald-500 transition-colors">›</span>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-white/80 text-sm font-medium truncate flex-1">{m.home_team}</span>
        {(m.status === 'live' || m.status === 'halftime' || m.status === 'fulltime')
          ? <span className="text-white font-bold text-sm tabular-nums px-1 flex-shrink-0">
              {m.home_score} - {m.away_score}
            </span>
          : <span className="text-white/20 text-xs px-1 flex-shrink-0">vs</span>
        }
        <span className="text-white/80 text-sm font-medium truncate flex-1 text-right">{m.away_team}</span>
      </div>
      {m.status === 'live' && m.match_minute > 0 && (
        <div className="mt-1.5 flex items-center gap-1">
          <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" />
          <span className="text-white/40 text-xs">{m.match_minute}'</span>
        </div>
      )}
    </div>
  </Link>
);

// ── Main Component ──────────────────────────────────────────────────────────

const MatchCenter = () => {
  const { id: matchId } = useParams();
  const [matchData, setMatchData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [error, setError] = useState(null);
  const [resumePosition, setResumePosition] = useState(null);
  const [reminderSet, setReminderSet] = useState(false);
  const [reminderLoading, setReminderLoading] = useState(false);
  const [inMyList, setInMyList] = useState(false);
  const [myListLoading, setMyListLoading] = useState(false);
  const [showMiniPlayer, setShowMiniPlayer] = useState(false);
  const [upNextMatches, setUpNextMatches] = useState([]);
  const [alertsEnabled, setAlertsEnabled] = useState(false);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [goalFlash, setGoalFlash] = useState(null); // {player, minute, team}

  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const wsRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const saveIntervalRef = useRef(null);
  const hasRestoredPosition = useRef(false);
  const seekDebounceRef = useRef(null);
  const playerSentinelRef = useRef(null);
  const lastDBSave = useRef(0);

  // ── Playback persistence ────────────────────────────────────────────────
  const getStorageKey = useCallback(() => {
    const user = localStorage.getItem('user');
    const userId = user ? JSON.parse(user)?.user_id : 'guest';
    return `afrosports_playback_${matchId}_${userId}`;
  }, [matchId]);

  const savePlaybackPosition = useCallback(() => {
    if (!videoRef.current || videoRef.current.paused) return;
    const currentTime = videoRef.current.currentTime;
    const duration = videoRef.current.duration;
    if (currentTime < 2 || (duration && currentTime > duration - 5)) return;
    try {
      localStorage.setItem(getStorageKey(), JSON.stringify({
        time: currentTime, duration: duration || 0, savedAt: Date.now(), matchId
      }));
    } catch (e) {}
  }, [matchId, getStorageKey]);

  const loadPlaybackPosition = useCallback(() => {
    try {
      const saved = localStorage.getItem(getStorageKey());
      if (!saved) return null;
      const data = JSON.parse(saved);
      if (data.matchId !== matchId) return null;
      if (Date.now() - data.savedAt > 24 * 60 * 60 * 1000) {
        localStorage.removeItem(getStorageKey());
        return null;
      }
      return data.time;
    } catch (e) { return null; }
  }, [matchId, getStorageKey]);

  const restorePlaybackPosition = useCallback(() => {
    if (!videoRef.current || hasRestoredPosition.current) return;
    // Priority: URL ?t= param (from Continue Watching) > localStorage
    const urlParams = new URLSearchParams(window.location.search);
    const urlTime = parseFloat(urlParams.get('t'));
    const savedTime = (urlTime && urlTime > 2) ? urlTime : loadPlaybackPosition();
    if (savedTime && savedTime > 2) {
      videoRef.current.currentTime = savedTime;
      setResumePosition(savedTime);
      hasRestoredPosition.current = true;
    }
  }, [loadPlaybackPosition]);

  const clearPlaybackPosition = useCallback(() => {
    try { localStorage.removeItem(getStorageKey()); } catch (e) {}
  }, [getStorageKey]);

  // ── Data fetching ────────────────────────────────────────────────────────
  const fetchMatchData = useCallback(async () => {
    try {
      const response = await fetch(`${API}/api/match/${matchId}`);
      if (!response.ok) throw new Error('Match not found');
      const data = await response.json();
      setMatchData(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [matchId]);

  const fetchUpNext = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/ticker/live`);
      if (res.ok) {
        const data = await res.json();
        setUpNextMatches(
          (data.matches || []).filter(m => m.content_id !== matchId).slice(0, 5)
        );
      }
    } catch (e) {}
  }, [matchId]);

  const checkReminderStatus = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await fetch(`${API}/api/reminders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setReminderSet((data.reminders || []).some(r => r.match_id === matchId));
      }
    } catch (e) {}
  }, [matchId]);

  const checkMyListStatus = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await fetch(`${API}/api/user/mylist`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setInMyList((data.content_ids || []).includes(matchId));
      }
    } catch (e) {}
  }, [matchId]);

  // ── Actions ──────────────────────────────────────────────────────────────
  const toggleMyList = async () => {
    const token = localStorage.getItem('token');
    if (!token) { toast.error('Please log in to use My List'); return; }
    setMyListLoading(true);
    try {
      const method = inMyList ? 'DELETE' : 'POST';
      const res = await fetch(`${API}/api/user/mylist/${matchId}`, {
        method, headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setInMyList(!inMyList);
        toast.success(inMyList ? 'Removed from My List' : 'Added to My List');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to update My List');
      }
    } catch { toast.error('Something went wrong'); }
    finally { setMyListLoading(false); }
  };

  const toggleReminder = async () => {
    const token = localStorage.getItem('token');
    if (!token) { toast.error('Please log in to set a reminder'); return; }
    setReminderLoading(true);
    try {
      const method = reminderSet ? 'DELETE' : 'POST';
      const res = await fetch(`${API}/api/reminders/${matchId}`, {
        method, headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setReminderSet(!reminderSet);
        toast.success(reminderSet ? 'Reminder removed' : "Reminder set! We'll email you 15 min before kick-off.");
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to update reminder');
      }
    } catch { toast.error('Something went wrong'); }
    finally { setReminderLoading(false); }
  };

  // ── Push Notification Alerts ─────────────────────────────────────────────
  const toggleAlerts = async () => {
    if (!isPushSupported()) {
      toast.error('Push notifications are not supported in this browser');
      return;
    }

    if (alertsEnabled) {
      // Unsubscribe
      setAlertsLoading(true);
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
          await fetch(`${API}/api/push/unsubscribe`, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ endpoint: sub.endpoint, match_id: matchId })
          });
        }
        setAlertsEnabled(false);
        // Remove from localStorage
        const subs = JSON.parse(localStorage.getItem('push_match_subs') || '[]');
        localStorage.setItem('push_match_subs', JSON.stringify(subs.filter(id => id !== matchId)));
        toast.success('Match alerts turned off');
      } catch (e) {
        toast.error('Could not disable alerts');
      } finally {
        setAlertsLoading(false);
      }
      return;
    }

    // Subscribe
    setAlertsLoading(true);
    try {
      // 1. Request permission
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        toast.error('Notification permission denied');
        setAlertsLoading(false);
        return;
      }

      // 2. Register service worker
      const reg = await navigator.serviceWorker.register('/sw.js');

      // 3. Get VAPID public key
      const keyRes = await fetch(`${API}/api/push/vapid-public-key`);
      const { publicKey } = await keyRes.json();

      // 4. Subscribe to push
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey)
      });

      // 5. Send subscription to backend
      const subJson = sub.toJSON();
      await fetch(`${API}/api/push/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          endpoint: subJson.endpoint,
          keys: subJson.keys,
          match_id: matchId
        })
      });

      setAlertsEnabled(true);
      // Persist to localStorage
      const subs = JSON.parse(localStorage.getItem('push_match_subs') || '[]');
      if (!subs.includes(matchId)) subs.push(matchId);
      localStorage.setItem('push_match_subs', JSON.stringify(subs));
      toast.success('Match alerts enabled! You\'ll be notified of goals & key events.');
    } catch (e) {
      console.error('Push subscribe error:', e);
      toast.error('Could not enable alerts. Try again.');
    } finally {
      setAlertsLoading(false);
    }
  };

  // Check if already subscribed on mount
  useEffect(() => {
    if (!isPushSupported()) return;
    // Use localStorage to track per-match subscription state
    const subs = JSON.parse(localStorage.getItem('push_match_subs') || '[]');
    setAlertsEnabled(subs.includes(matchId));
  }, [matchId]);

  // Handle push notification tap — show goal overlay immediately
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const evtType = params.get('event');
    if (evtType === 'goal' || evtType === 'penalty') {
      // Clean URL without reload
      window.history.replaceState({}, '', window.location.pathname);
      // Scroll to top to reveal the full video player
      window.scrollTo({ top: 0, behavior: 'smooth' });
      // Show goal flash once match data has loaded (set pending)
      const player = decodeURIComponent(params.get('player') || '');
      const minute = params.get('minute') || '';
      // Delay slightly to let matchData load
      setTimeout(() => {
        setGoalFlash({ player, minute, fromNotification: true });
      }, 800);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── WebSocket ────────────────────────────────────────────────────────────
  const connectWebSocket = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;
    try {
      const ws = new WebSocket(`${WS_URL}/api/ws/match/${matchId}`);
      wsRef.current = ws;

      ws.onopen = () => {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'pong') return;
          if (data.type === 'event') {
            const evt = data.event;
            setMatchData(prev => ({
              ...prev,
              events: [...(prev?.events || []), evt].sort((a, b) => a.minute - b.minute)
            }));
            // Show GOAL! broadcast overlay for goal/penalty events
            if (evt.event_type === 'goal' || evt.event_type === 'penalty') {
              setGoalFlash({ player: evt.player_name, minute: evt.minute, team: evt.team });
            }
          }
          if (data.score) {
            setMatchData(prev => ({
              ...prev,
              teams: {
                ...prev?.teams,
                home: { ...prev?.teams?.home, score: data.score.home_score || 0 },
                away: { ...prev?.teams?.away, score: data.score.away_score || 0 }
              },
              clock: {
                ...prev?.clock,
                minute: data.score.match_minute || prev?.clock?.minute || 0,
                status: data.score.status || prev?.clock?.status || 'upcoming'
              }
            }));
          }
        } catch (e) {}
      };

      ws.onclose = () => {
        wsRef.current = null;
        if (!pollIntervalRef.current) {
          pollIntervalRef.current = setInterval(fetchMatchData, 15000);
        }
        setTimeout(connectWebSocket, 5000);
      };

      ws.onerror = () => ws.close();

      const pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send('ping');
      }, 25000);
      ws.pingInterval = pingInterval;
    } catch (err) {
      if (!pollIntervalRef.current) {
        pollIntervalRef.current = setInterval(fetchMatchData, 15000);
      }
    }
  }, [matchId, fetchMatchData]);

  // ── HLS video ────────────────────────────────────────────────────────────
  const [videoError, setVideoError] = useState(false);
  
  const initVideo = useCallback(() => {
    if (!matchData?.match?.video_url || !videoRef.current) {
      if (matchData?.match && !matchData.match.video_url) {
        console.warn('No video URL available for this match');
        setVideoError(true);
      }
      return;
    }
    setVideoError(false);

    if (Hls.isSupported()) {
      if (hlsRef.current) hlsRef.current.destroy();
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: true,
        liveSyncDuration: 3,
        liveMaxLatencyDuration: 10,
        manifestLoadingMaxRetry: 6,
        manifestLoadingRetryDelay: 2000,
        levelLoadingMaxRetry: 6,
        fragLoadingMaxRetry: 6,
      });
      hls.loadSource(matchData.match.video_url);
      hls.attachMedia(videoRef.current);
      hls.on(Hls.Events.MANIFEST_PARSED, () => restorePlaybackPosition());
      hls.on(Hls.Events.ERROR, (event, data) => {
        if (data.fatal) {
          console.error('HLS fatal error:', data.type, data.details);
          if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
            console.log('Retrying HLS load...');
            hls.startLoad();
          } else {
            setVideoError(true);
            hls.destroy();
          }
        }
      });
      hlsRef.current = hls;
    } else if (videoRef.current.canPlayType('application/vnd.apple.mpegurl')) {
      videoRef.current.src = matchData.match.video_url;
      videoRef.current.addEventListener('loadedmetadata', restorePlaybackPosition, { once: true });
      videoRef.current.addEventListener('error', () => setVideoError(true), { once: true });
    }

    const video = videoRef.current;
    video.addEventListener('ended', clearPlaybackPosition);
    return () => { video.removeEventListener('ended', clearPlaybackPosition); };
  }, [matchData?.match?.video_url, restorePlaybackPosition, clearPlaybackPosition]);

  // ── Scroll-based mini-player detection ───────────────────────────────────
  useEffect(() => {
    if (!matchData?.match?.video_url) return;

    const handleScroll = () => {
      if (!playerSentinelRef.current) return;
      const rect = playerSentinelRef.current.getBoundingClientRect();
      // Show mini-player when less than 40% of the player is visible in the viewport
      const visible = Math.max(0, Math.min(window.innerHeight, rect.bottom) - Math.max(0, rect.top));
      const ratio = visible / rect.height;
      setShowMiniPlayer(ratio < 0.4);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // run once on mount
    return () => window.removeEventListener('scroll', handleScroll);
  }, [matchData?.match?.video_url]);

  // ── Main effect ──────────────────────────────────────────────────────────
  useEffect(() => {
    fetchMatchData();
    connectWebSocket();
    checkReminderStatus();
    checkMyListStatus();
    fetchUpNext();
    saveIntervalRef.current = setInterval(savePlaybackPosition, 5000);

    return () => {
      if (wsRef.current) {
        if (wsRef.current.pingInterval) clearInterval(wsRef.current.pingInterval);
        wsRef.current.close();
      }
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      if (saveIntervalRef.current) clearInterval(saveIntervalRef.current);
      if (seekDebounceRef.current) clearTimeout(seekDebounceRef.current);
      if (hlsRef.current) hlsRef.current.destroy();
      savePlaybackPosition();
      // Save to DB on unmount so Continue Watching works
      const token = localStorage.getItem('token');
      if (token && videoRef.current && videoRef.current.currentTime > 2) {
        const video = videoRef.current;
        const duration = (video.duration && isFinite(video.duration)) ? video.duration : 5400;
        fetch(`${API}/api/user/watch-history`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ content_id: matchId, position: video.currentTime, duration }),
          keepalive: true
        }).catch(() => {});
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { initVideo(); }, [initVideo]);

  // ── Video controls ───────────────────────────────────────────────────────
  const togglePlay = () => {
    if (!videoRef.current) return;
    if (isPlaying) videoRef.current.pause();
    else videoRef.current.play();
    setIsPlaying(!isPlaying);
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  const toggleFullscreen = () => {
    if (!videoRef.current) return;
    if (document.fullscreenElement) document.exitFullscreen();
    else videoRef.current.requestFullscreen();
  };

  // ── States ───────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <Navbar />
        <LiveTicker />
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-emerald-500" />
        </div>
      </div>
    );
  }

  if (error || !matchData) {
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <Navbar />
        <LiveTicker />
        <div className="flex flex-col items-center justify-center min-h-screen gap-4">
          <p className="text-white/50 text-lg">Match not found</p>
          <Link to="/" className="bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2 rounded-lg transition-colors">
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  const { match, teams, clock, events = [], stats, lineups, commentary = [] } = matchData;

  // Build combined chronological feed (newest first)
  const feed = [
    ...(events || []).map(e => ({
      ...e, _type: 'event', _sort: e.minute * 100 + (e.added_time || 0)
    })),
    ...(commentary || []).map(c => ({
      ...c, _type: 'commentary', _sort: c.minute * 100 + (c.added_time || 0)
    }))
  ].sort((a, b) => b._sort - a._sort);

  const hasVideo = !!match.video_url;

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="match-center">
      <Navbar />
      <LiveTicker />

      <main className="pt-28 pb-16">

        {/* Back button */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-3">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-white/30 hover:text-white/80 text-sm transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back
          </Link>
        </div>

        {/* ── VIDEO PLAYER ─────────────────────────────────────────────────── */}
        {hasVideo && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-0">

            {/*
              playerSentinelRef — this div ALWAYS occupies the 16:9 space in the layout.
              When showMiniPlayer is true, the inner video wrapper becomes position:fixed
              so the content doesn't jump. React reuses the <video> DOM node — no remount.
            */}
            <div
              ref={playerSentinelRef}
              className={`relative aspect-video rounded-xl overflow-hidden ${showMiniPlayer ? 'bg-[#111] border border-white/5' : 'bg-black'}`}
              data-testid="video-player"
            >
              {/* Inner container — transitions between inline and fixed mini-player */}
              <div
                className={`group transition-all duration-300 ${
                  showMiniPlayer
                    ? 'fixed bottom-6 right-6 z-[100] w-64 sm:w-72 aspect-video rounded-xl overflow-hidden shadow-[0_0_40px_rgba(0,0,0,0.8)] border border-white/20 bg-black'
                    : 'absolute inset-0 rounded-xl overflow-hidden bg-black'
                }`}
              >
                {/* The video element — stays mounted through transitions */}
                <video
                  ref={videoRef}
                  className="w-full h-full object-contain bg-black"
                  poster={match.thumbnail_url}
                  playsInline
                  onPlay={() => {
                    setIsPlaying(true);
                    trackEvent('video_play', matchId, { position: videoRef.current?.currentTime || 0 });
                  }}
                  onPause={() => setIsPlaying(false)}
                  onSeeked={() => {
                    if (seekDebounceRef.current) clearTimeout(seekDebounceRef.current);
                    seekDebounceRef.current = setTimeout(() => {
                      trackEvent('video_seek', matchId, { position: videoRef.current?.currentTime || 0 });
                    }, 1000);
                  }}
                  onTimeUpdate={() => {
                    if (resumePosition && videoRef.current?.currentTime > resumePosition + 3) {
                      setResumePosition(null);
                    }
                    // Save watch progress to DB (throttled every 15s)
                    const token = localStorage.getItem('token');
                    if (token && videoRef.current && videoRef.current.currentTime > 2) {
                      const now = Date.now();
                      if (now - lastDBSave.current >= 15000) {
                        lastDBSave.current = now;
                        const video = videoRef.current;
                        const duration = (video.duration && isFinite(video.duration)) ? video.duration : 5400;
                        fetch(`${API}/api/user/watch-history`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                          body: JSON.stringify({ content_id: matchId, position: video.currentTime, duration })
                        }).catch(() => {});
                      }
                    }
                  }}
                />

                {/* Video error / loading fallback overlay */}
                {videoError && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/90 z-10" data-testid="video-error-overlay">
                    <img src={match.thumbnail_url} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
                    <div className="relative z-10 text-center p-6">
                      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-white/10 flex items-center justify-center">
                        <Play className="w-8 h-8 text-white/50" />
                      </div>
                      <p className="text-white font-semibold mb-1">Stream unavailable</p>
                      <p className="text-white/40 text-sm">The live stream is not currently active. Check back soon.</p>
                    </div>
                  </div>
                )}

                {/* ── GOAL! BROADCAST OVERLAY ── renders over video in both modes */}
                {goalFlash && (
                  <GoalFlash
                    flash={goalFlash}
                    teams={matchData?.teams}
                    mini={showMiniPlayer}
                    onDone={() => setGoalFlash(null)}
                  />
                )}

                {/* ── MAIN PLAYER OVERLAYS (normal mode only) ── */}
                {!showMiniPlayer && (
                  <>
                    {/* Hover controls gradient */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />

                    {/* Bottom control bar */}
                    <div className="absolute bottom-0 left-0 right-0 p-4 flex items-center justify-between opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={togglePlay}
                          className="w-10 h-10 bg-white/20 hover:bg-emerald-600/70 rounded-full flex items-center justify-center transition-colors backdrop-blur-sm"
                          data-testid="play-button"
                        >
                          {isPlaying
                            ? <Pause className="w-5 h-5 text-white" />
                            : <Play className="w-5 h-5 text-white fill-white" />}
                        </button>
                        <button
                          onClick={toggleMute}
                          className="text-white hover:text-emerald-400 transition-colors"
                          data-testid="mute-button"
                        >
                          {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                        </button>
                      </div>
                      <button
                        onClick={toggleFullscreen}
                        className="text-white hover:text-emerald-400 transition-colors"
                        data-testid="fullscreen-button"
                      >
                        <Maximize className="w-5 h-5" />
                      </button>
                    </div>

                    {/* Live badge top-left */}
                    {match.is_live && (
                      <div className="absolute top-4 left-4 pointer-events-none">
                        <LiveBadge variant="default" />
                      </div>
                    )}

                    {/* Resume indicator */}
                    {resumePosition && (
                      <div
                        className="absolute top-4 right-4 bg-emerald-600/90 backdrop-blur-sm text-white text-xs px-3 py-1.5 rounded-lg flex items-center gap-1.5"
                        data-testid="resume-indicator"
                      >
                        <Play className="w-3 h-3 fill-white" />
                        Resumed at {Math.floor(resumePosition / 60)}:{String(Math.floor(resumePosition % 60)).padStart(2, '0')}
                      </div>
                    )}
                  </>
                )}

                {/* ── MINI-PLAYER OVERLAY ── */}
                {showMiniPlayer && (
                  <div className="absolute top-0 inset-x-0 flex items-center justify-between px-2 py-1.5 bg-gradient-to-b from-black/90 to-transparent">
                    <span className="text-white text-xs font-medium truncate flex-1 mr-2">
                      {teams?.home?.name} · {teams?.away?.name}
                    </span>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                        className="w-5 h-5 bg-white/15 hover:bg-white/30 rounded flex items-center justify-center transition-colors"
                        title="Back to full player"
                        data-testid="mini-player-expand"
                      >
                        <Maximize2 className="w-3 h-3 text-white" />
                      </button>
                      <button
                        onClick={() => setShowMiniPlayer(false)}
                        className="w-5 h-5 bg-white/15 hover:bg-white/30 rounded flex items-center justify-center transition-colors"
                        title="Close mini player"
                        data-testid="mini-player-close"
                      >
                        <X className="w-3 h-3 text-white" />
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Placeholder content when mini-player is active */}
              {showMiniPlayer && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <button
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    className="text-white/20 hover:text-white/50 text-xs transition-colors flex items-center gap-2"
                  >
                    <Play className="w-4 h-4" />
                    Playing in mini-player
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── NO VIDEO FALLBACK ─────────────────────────────────────────────── */}
        {!hasVideo && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-0">
            <div className="relative aspect-video rounded-xl overflow-hidden bg-[#111]" data-testid="no-video-hero">
              <img src={match.thumbnail_url} alt={match.teams || match.title} className="w-full h-full object-cover opacity-40" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/20" />
              {match.is_live && (
                <div className="absolute top-4 left-4">
                  <LiveBadge variant="default" />
                </div>
              )}
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6">
                <div className="w-16 h-16 mb-4 rounded-full bg-white/10 border border-white/20 flex items-center justify-center">
                  <Play className="w-8 h-8 text-white/50" />
                </div>
                <p className="text-white font-semibold text-lg mb-1">{match.is_live ? 'Stream starting soon' : 'Video not yet available'}</p>
                <p className="text-white/40 text-sm">{match.is_live ? 'The broadcast will begin shortly. Stay tuned!' : 'Check back later for the replay.'}</p>
              </div>
            </div>
          </div>
        )}

        {/* ── SCOREBOARD ───────────────────────────────────────────────────── */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-1.5">
          <div
            className="bg-[#0d1a12] border border-emerald-900/40 rounded-xl px-5 py-4 mb-5"
            data-testid="scoreboard"
          >
            {/* Competition label */}
            <div className="text-center text-emerald-500/70 text-xs font-semibold uppercase tracking-widest mb-3">
              {match.competition}
            </div>

            <div className="flex items-center">
              {/* Home team */}
              <div className="flex items-center gap-3 flex-1 min-w-0" data-testid="home-team">
                <img
                  src={teams.home.badge_url}
                  alt={teams.home.name}
                  className="w-12 h-12 sm:w-14 sm:h-14 rounded-full flex-shrink-0 ring-2 ring-white/5"
                />
                <span className="text-white font-bold text-base sm:text-lg truncate hidden sm:block">
                  {teams.home.name}
                </span>
              </div>

              {/* Score + clock */}
              <div className="flex flex-col items-center px-4 flex-shrink-0" data-testid="match-score">
                <div className="flex items-center gap-3 sm:gap-4 leading-none">
                  <span className={`text-4xl sm:text-5xl font-black tabular-nums ${teams.home.score > teams.away.score ? 'text-emerald-400' : 'text-white'}`}>
                    {teams.home.score}
                  </span>
                  <span className="text-white/20 text-2xl font-light">:</span>
                  <span className={`text-4xl sm:text-5xl font-black tabular-nums ${teams.away.score > teams.home.score ? 'text-emerald-400' : 'text-white'}`}>
                    {teams.away.score}
                  </span>
                </div>

                {/* Match clock */}
                <div className="mt-2 flex items-center gap-2" data-testid="match-clock">
                  {clock.status === 'live' ? (
                    <div className="flex items-center gap-1.5">
                      <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                      <span className="text-white font-mono font-bold text-sm">
                        {clock.minute}'{clock.added_time > 0 ? `+${clock.added_time}` : ''}
                      </span>
                    </div>
                  ) : clock.status === 'halftime' ? (
                    <span className="text-yellow-400 font-bold text-xs tracking-widest">HALF TIME</span>
                  ) : clock.status === 'fulltime' ? (
                    <span className="text-white/40 font-bold text-xs tracking-widest">FULL TIME</span>
                  ) : (
                    <span className="text-emerald-400 font-semibold text-xs">
                      {match.start_time
                        ? new Date(match.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                        : 'UPCOMING'}
                    </span>
                  )}
                </div>
              </div>

              {/* Away team */}
              <div className="flex items-center gap-3 flex-1 min-w-0 justify-end" data-testid="away-team">
                <span className="text-white font-bold text-base sm:text-lg truncate hidden sm:block text-right">
                  {teams.away.name}
                </span>
                <img
                  src={teams.away.badge_url}
                  alt={teams.away.name}
                  className="w-12 h-12 sm:w-14 sm:h-14 rounded-full flex-shrink-0 ring-2 ring-white/5"
                />
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex items-center justify-center gap-2 mt-4 pt-3 border-t border-white/5">
              {!match.is_live && match.start_time && (
                <button
                  onClick={toggleReminder}
                  disabled={reminderLoading}
                  data-testid="remind-me-button"
                  className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all ${
                    reminderSet
                      ? 'bg-emerald-600/20 border border-emerald-500/60 text-emerald-400'
                      : 'bg-white/5 border border-white/15 text-white/50 hover:border-emerald-500/40 hover:text-emerald-400'
                  }`}
                >
                  {reminderLoading
                    ? <span className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    : reminderSet
                    ? <BellOff className="w-3.5 h-3.5" />
                    : <Bell className="w-3.5 h-3.5" />}
                  {reminderSet ? 'Reminder Set' : 'Remind Me'}
                </button>
              )}
              <button
                onClick={toggleMyList}
                disabled={myListLoading}
                data-testid="mylist-button"
                className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all ${
                  inMyList
                    ? 'bg-emerald-600/20 border border-emerald-500/60 text-emerald-400'
                    : 'bg-white/5 border border-white/15 text-white/50 hover:border-emerald-500/40 hover:text-emerald-400'
                }`}
              >
                {myListLoading
                  ? <span className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  : inMyList
                  ? <BookmarkCheck className="w-3.5 h-3.5" />
                  : <BookmarkPlus className="w-3.5 h-3.5" />}
                {inMyList ? 'In My List' : '+ My List'}
              </button>

              {/* Match Alerts — push notification button (live matches only) */}
              {match.is_live && isPushSupported() && (
                <button
                  onClick={toggleAlerts}
                  disabled={alertsLoading}
                  data-testid="match-alerts-button"
                  className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all ${
                    alertsEnabled
                      ? 'bg-red-600/20 border border-red-500/60 text-red-400'
                      : 'bg-white/5 border border-white/15 text-white/50 hover:border-red-500/40 hover:text-red-400'
                  }`}
                >
                  {alertsLoading
                    ? <span className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    : alertsEnabled
                    ? <Radio className="w-3.5 h-3.5 animate-pulse" />
                    : <Bell className="w-3.5 h-3.5" />}
                  {alertsEnabled ? 'Alerts On' : 'Match Alerts'}
                </button>
              )}
            </div>
          </div>

          {/* ── TWO-COLUMN LAYOUT ─────────────────────────────────────────── */}
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-5">

            {/* ── LEFT: Live Match Center ─────────────────────────────────── */}
            <div className="min-h-[600px]">
              {/* Section heading */}
              <div className="flex items-center gap-2 mb-3">
                {clock.status === 'live' && (
                  <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse flex-shrink-0" />
                )}
                <h2 className="text-white/70 font-bold text-xs uppercase tracking-widest">
                  Live Match Center
                </h2>
              </div>

              {/* Combined event + commentary feed */}
              <div className="space-y-1.5 mb-4" data-testid="live-feed">
                {feed.length === 0 ? (
                  <div className="bg-[#111111] rounded-xl p-10 text-center text-white/20 text-sm">
                    Match events will appear here as the game unfolds
                  </div>
                ) : (
                  feed.map((item, idx) => {
                    if (item._type === 'commentary') {
                      return (
                        <div
                          key={`c-${item.comment_id || idx}`}
                          className={`px-4 py-3 rounded-lg ${item.is_key_event ? 'bg-emerald-900/20 border-l-4 border-emerald-500' : 'bg-[#111111]'}`}
                          data-testid={`commentary-${idx}`}
                        >
                          <div className="flex gap-3">
                            <span className="text-emerald-500/60 font-mono text-xs font-bold whitespace-nowrap pt-0.5 min-w-[32px]">
                              {item.minute}'
                            </span>
                            <p className="text-white/60 text-sm leading-relaxed">{item.text}</p>
                          </div>
                        </div>
                      );
                    }

                    // Event item
                    const cfg = EVENT_CFG[item.event_type] || {
                      label: (item.event_type || '').replace(/_/g, ' ').toUpperCase(),
                      emoji: '📌',
                      rowCls: 'bg-[#111111]',
                      timeCls: 'text-white/50',
                      labelCls: 'text-white/50 text-xs',
                      big: false
                    };

                    return (
                      <div
                        key={`e-${item.event_id || idx}`}
                        className={`px-4 ${cfg.big ? 'py-4' : 'py-3'} rounded-lg ${cfg.rowCls}`}
                        data-testid={`event-${item.event_id || idx}`}
                      >
                        <div className="flex items-start gap-3">
                          <span className={`font-mono text-xs font-bold whitespace-nowrap pt-0.5 min-w-[32px] ${cfg.timeCls}`}>
                            {item.minute}'{item.added_time > 0 ? `+${item.added_time}` : ''}
                          </span>
                          <span className="text-base leading-none flex-shrink-0 mt-0.5">{cfg.emoji}</span>
                          <div className="flex-1 min-w-0">
                            <span className={`block font-bold uppercase ${cfg.labelCls}`}>{cfg.label}</span>
                            {item.player_name && (
                              <p className={`font-semibold mt-0.5 ${cfg.big ? 'text-white text-base' : 'text-white/80 text-sm'}`}>
                                {item.player_name}
                              </p>
                            )}
                            {item.description && (
                              <p className="text-white/40 text-xs mt-0.5">{item.description}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* ── LINEUPS ACCORDION ─── */}
              <AccordionSection title="Team Lineups" icon={Users} testId="accordion-lineups">
                {lineups ? (
                  <div className="grid md:grid-cols-2 gap-6 pt-3" data-testid="lineups-content">
                    {/* Home */}
                    <div>
                      <h4 className="flex items-center gap-2 text-white/80 font-semibold text-xs uppercase tracking-wider mb-3">
                        <img src={teams.home.badge_url} alt="" className="w-4 h-4 rounded-full" />
                        {teams.home.name}
                        <span className="text-white/30 normal-case font-normal">({lineups.home?.formation || '4-3-3'})</span>
                      </h4>
                      <div className="space-y-0.5">
                        {lineups.home?.players?.filter(p => p.is_starter).map((player, i) => (
                          <PlayerRow key={i} player={player} accentColor="bg-emerald-700" />
                        ))}
                      </div>
                      {lineups.home?.players?.some(p => !p.is_starter) && (
                        <>
                          <p className="text-white/20 text-xs uppercase tracking-wider mt-3 mb-1.5">Subs</p>
                          <div className="space-y-0.5 opacity-50">
                            {lineups.home.players.filter(p => !p.is_starter).map((player, i) => (
                              <PlayerRow key={i} player={player} accentColor="bg-gray-700" />
                            ))}
                          </div>
                        </>
                      )}
                    </div>

                    {/* Away */}
                    <div>
                      <h4 className="flex items-center gap-2 text-white/80 font-semibold text-xs uppercase tracking-wider mb-3">
                        <img src={teams.away.badge_url} alt="" className="w-4 h-4 rounded-full" />
                        {teams.away.name}
                        <span className="text-white/30 normal-case font-normal">({lineups.away?.formation || '4-3-3'})</span>
                      </h4>
                      <div className="space-y-0.5">
                        {lineups.away?.players?.filter(p => p.is_starter).map((player, i) => (
                          <PlayerRow key={i} player={player} accentColor="bg-orange-700" />
                        ))}
                      </div>
                      {lineups.away?.players?.some(p => !p.is_starter) && (
                        <>
                          <p className="text-white/20 text-xs uppercase tracking-wider mt-3 mb-1.5">Subs</p>
                          <div className="space-y-0.5 opacity-50">
                            {lineups.away.players.filter(p => !p.is_starter).map((player, i) => (
                              <PlayerRow key={i} player={player} accentColor="bg-gray-700" />
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="text-white/25 text-sm text-center py-6 pt-4">Lineups not yet available</p>
                )}
              </AccordionSection>

              {/* ── STATS ACCORDION ─── */}
              <AccordionSection title="Match Stats" icon={BarChart3} testId="accordion-stats">
                {stats ? (
                  <div className="space-y-4 pt-3" data-testid="stats-content">
                    {[
                      { label: 'Possession', home: stats.home_possession ?? 50, away: stats.away_possession ?? 50, suffix: '%' },
                      { label: 'Shots', home: stats.home_shots ?? 0, away: stats.away_shots ?? 0 },
                      { label: 'On Target', home: stats.home_shots_on_target ?? 0, away: stats.away_shots_on_target ?? 0 },
                      { label: 'Corners', home: stats.home_corners ?? 0, away: stats.away_corners ?? 0 },
                      { label: 'Fouls', home: stats.home_fouls ?? 0, away: stats.away_fouls ?? 0 },
                      { label: 'Offsides', home: stats.home_offsides ?? 0, away: stats.away_offsides ?? 0 },
                      { label: 'Yellow Cards', home: stats.home_yellow_cards ?? 0, away: stats.away_yellow_cards ?? 0 },
                      { label: 'Red Cards', home: stats.home_red_cards ?? 0, away: stats.away_red_cards ?? 0 },
                    ].map((s, i) => <StatBar key={i} {...s} />)}
                  </div>
                ) : (
                  <p className="text-white/25 text-sm text-center py-6 pt-4">Stats not yet available</p>
                )}
              </AccordionSection>
            </div>

            {/* ── RIGHT: Sidebar ──────────────────────────────────────────── */}
            <div>
              <h2 className="text-white/70 font-bold text-xs uppercase tracking-widest mb-3">
                Up Next
              </h2>

              <div data-testid="up-next-section">
                {upNextMatches.length === 0 ? (
                  <div className="bg-[#111111] rounded-xl p-6 text-center text-white/20 text-sm">
                    No other matches available
                  </div>
                ) : (
                  <div className="space-y-2">
                    {upNextMatches.map((m, idx) => (
                      <UpNextCard key={m.content_id || idx} match={m} idx={idx} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default MatchCenter;
